import React, { useState, useEffect, useMemo } from 'react';
import { Heart, ShoppingCart, ExternalLink, Share2, MapPin, Clock, Star, ChevronLeft, ChevronRight, Plus, Bookmark, AlertTriangle, CheckCircle, Info, Sparkles, TrendingUp, History, Users } from 'lucide-react';
import { Modal } from '../ui';
import propSimilarityService from '../../utils/propSimilarityService';
import { getPropImagePath, getPropImagePaths, extractImageSource } from '../../utils/imageUtils';

const PropDetailsModal = ({ prop, onClose, onAddToCart, onAddToList, onRequestTransfer, userLists = [], allProps = [], onPropClick }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showListMenu, setShowListMenu] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [activeRecommendationTab, setActiveRecommendationTab] = useState('similar');
  
  // Reset state and generate recommendations when prop changes
  const sliderImages = useMemo(() => {
    if (!prop) return ['/images/fallbacks/prop-default.jpg'];

    const heroImage = getPropImagePath(prop);

    const allImages = getPropImagePaths(prop);
    const normalized = [];

    const push = (src) => {
      if (!src) return;
      if (normalized.includes(src)) return;
      normalized.push(src);
    };

    push(heroImage);
    allImages.forEach((image) => {
      if (image === heroImage) return;
      push(image);
    });

    if (!normalized.length) {
      normalized.push('/images/fallbacks/prop-default.jpg');
    }

    return normalized;
  }, [prop]);

  useEffect(() => {
    if (prop) {
      setCurrentImageIndex(0); // Reset to first image when prop changes
      
      // Generate AI recommendations
      if (allProps.length > 0) {
        const strategies = propSimilarityService.getRecommendationStrategies(prop, allProps);
        setRecommendations(strategies);
        console.log('🤖 AI Recommendations generated:', strategies);
      }
    }
  }, [prop, allProps]);
  
  if (!prop) return null;

  const normalizedStatus = (prop.status || '').toLowerCase();
  const cartAvailable = normalizedStatus === 'available';

  const images = sliderImages;
  
  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };
  
  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-800 border-green-200';
      case 'checked_out': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'reserved': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'maintenance': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'available': return <CheckCircle className="h-4 w-4" />;
      case 'checked_out': return <Clock className="h-4 w-4" />;
      case 'reserved': return <Bookmark className="h-4 w-4" />;
      case 'maintenance': return <AlertTriangle className="h-4 w-4" />;
      default: return <Info className="h-4 w-4" />;
    }
  };

  return (
    <Modal 
      isOpen={!!prop} 
      onClose={onClose} 
      size="full"
      showCloseButton={false}
    >
      <Modal.Header onClose={onClose}>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{prop.name}</h1>
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <div className="flex items-center gap-1">
            <MapPin className="h-4 w-4" />
            <span>{prop.owner} • {Math.round(prop.distance || 0)} miles away</span>
          </div>
          <div className={`flex items-center gap-1 px-3 py-1 rounded-full border ${getStatusColor(prop.status)}`}>
            {getStatusIcon(prop.status)}
            <span className="font-medium capitalize">{prop.status}</span>
          </div>
        </div>
      </Modal.Header>

      <Modal.Content>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 pb-8 lg:pb-8">
            
            {/* Left Column - Images & Actions */}
            <div className="lg:col-span-2 space-y-6">
              {/* Image Gallery */}
              <div className="relative">
                <div className="aspect-video rounded-xl overflow-hidden bg-gray-100 relative group">
                  <div
                    className="flex h-full w-full transition-transform duration-500 ease-out"
                    style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
                  >
                    {images.map((image, index) => (
                      <div key={`${image}-${index}`} className="w-full flex-shrink-0 h-full min-h-0">
                        <div className="w-full h-full flex items-center justify-center bg-gray-100 min-h-0">
                          <img
                            src={image}
                            alt={`${prop.name} ${index + 1}`}
                            className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Image Navigation */}
                  {images.length > 1 && (
                    <>
                      <button 
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full transition-all"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button 
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full transition-all"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                      
                      {/* Image Indicators */}
                      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                        {images.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setCurrentImageIndex(index)}
                            className={`w-2 h-2 rounded-full transition-all ${
                              index === currentImageIndex ? 'bg-white' : 'bg-white bg-opacity-50'
                            }`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
                
                {/* Thumbnail Strip */}
                {images.length > 1 && (
                  <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                    {images.map((image, index) => (
                      <button
                        key={`${image}-${index}`}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                          index === currentImageIndex ? 'border-blue-500' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <img src={image} alt={`${prop.name} ${index + 1}`} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                {onAddToCart && (
                  <button
                    onClick={() => {
                      if (!cartAvailable) return;
                      onAddToCart(prop.id);
                    }}
                    disabled={!cartAvailable}
                    title={cartAvailable ? 'Add to cart' : 'Currently unavailable'}
                    aria-disabled={!cartAvailable}
                    className={[
                      'flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all transform',
                      cartAvailable ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:scale-105' : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    ].join(' ')}
                  >
                    <ShoppingCart className="h-5 w-5" />
                    Add to Cart
                  </button>
                )}
                
                {/* Add to List temporarily hidden while the feature is reshaped */}
                <div className="relative hidden" aria-hidden="true">
                  <button 
                    onClick={() => setShowListMenu(!showListMenu)}
                    className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-medium transition-all"
                  >
                    <Plus className="h-5 w-5" />
                    Add to List
                  </button>

                  {showListMenu && (
                    <div className="absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg py-2 z-10 min-w-48">
                      {userLists.length > 0 ? (
                        userLists.map(list => (
                          <button
                            key={list.id}
                            onClick={() => {
                              onAddToList && onAddToList(prop.id, list.id);
                              setShowListMenu(false);
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm"
                          >
                            {list.name}
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-sm text-gray-500">No lists available</div>
                      )}
                    </div>
                  )}
                </div>
                
                {onRequestTransfer && (
                  <button 
                    onClick={() => onRequestTransfer(prop)}
                    className="flex items-center gap-2 bg-purple-100 hover:bg-purple-200 text-purple-700 px-6 py-3 rounded-xl font-medium transition-all"
                  >
                    <ExternalLink className="h-5 w-5" />
                    Request Transfer
                  </button>
                )}
                
                <div className="relative">
                  <button 
                    onClick={() => setShowShareMenu(!showShareMenu)}
                    className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-medium transition-all"
                  >
                    <Share2 className="h-5 w-5" />
                    Share
                  </button>
                  
                  {showShareMenu && (
                    <div className="absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg py-2 z-10">
                      <button className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm">Copy Link</button>
                      <button className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm">Download Image</button>
                    </div>
                  )}
                </div>
              </div>
              
            </div>

            {/* Right Column - Details */}
            <div className="space-y-4 lg:space-y-6 pb-6">
              {/* Warehouse & Availability */}
              <div className="bg-white border border-gray-200 rounded-xl p-4 lg:p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Item Overview</h3>
                <div className="space-y-3 text-sm text-gray-600">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Warehouse</span>
                    <span className="font-medium">{prop.organization?.name || prop.owner || 'Unknown'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Availability Status</span>
                    <span className="font-medium capitalize">{prop.status || 'Unknown'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Item Type</span>
                    <span className="font-medium">{prop.category || 'Uncategorized'}</span>
                  </div>
                  {prop.usage && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Usage</span>
                      <span className="font-medium">{prop.usage}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Current Condition</span>
                    <span className="font-medium">{prop.condition || 'Not specified'}</span>
                  </div>
                </div>
              </div>

              {/* Visual Descriptors */}
              {(prop.size || (prop.colors && prop.colors.length > 0) || (prop.material && prop.material.length > 0)) && (
                <div className="bg-white border border-gray-200 rounded-xl p-4 lg:p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Visual Descriptors</h3>
                  <div className="space-y-3 text-sm text-gray-600">
                    {prop.size && (
                      <div className="flex justify-between">
                        <span>Size</span>
                        <span className="font-medium capitalize">{prop.size}</span>
                      </div>
                    )}
                    {prop.colors && prop.colors.length > 0 && (
                      <div>
                        <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">Colors</div>
                        <div className="flex flex-wrap gap-2">
                          {prop.colors.map(color => (
                            <span key={color} className="px-3 py-1 rounded-full bg-gray-100 text-xs font-semibold uppercase tracking-wide text-gray-700">
                              {color}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {prop.material && Array.isArray(prop.material) && prop.material.length > 0 && (
                      <div>
                        <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">Materials</div>
                        <div className="flex flex-wrap gap-2">
                          {prop.material.map(material => (
                            <span key={material} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                              {material}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Usage & Context */}
              {(prop.usage_category_name || prop.use_how || prop.use_when || prop.use_where) && (
                <div className="bg-white border border-gray-200 rounded-xl p-4 lg:p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Usage & Context</h3>
                  <div className="space-y-3 text-sm text-gray-600">
                    {prop.usage_category_name && (
                      <div className="flex justify-between">
                        <span>Usage Category</span>
                        <span className="font-medium">{prop.usage_category_name}</span>
                      </div>
                    )}
                    {prop.use_how && (
                      <div>
                        <div className="text-xs text-gray-500">How</div>
                        <p className="text-sm text-gray-700">{prop.use_how}</p>
                      </div>
                    )}
                    {prop.use_when && (
                      <div>
                        <div className="text-xs text-gray-500">When</div>
                        <p className="text-sm text-gray-700">{prop.use_when}</p>
                      </div>
                    )}
                    {prop.use_where && (
                      <div>
                        <div className="text-xs text-gray-500">Where</div>
                        <p className="text-sm text-gray-700">{prop.use_where}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Consumable / Modifications */}
              {(prop.is_consumable !== undefined || prop.modification_allowed !== undefined) && (
                <div className="bg-white border border-gray-200 rounded-xl p-4 lg:p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Usage Rules</h3>
                  <div className="space-y-3 text-sm text-gray-600">
                    <div className="flex justify-between">
                      <span>Consumable</span>
                      <span className="font-medium">{prop.is_consumable ? 'Yes' : 'No'}</span>
                    </div>
                    {prop.is_consumable && prop.consumable_policy && (
                      <div className="flex justify-between">
                        <span>Consumable Policy</span>
                        <span className="font-medium capitalize">{prop.consumable_policy}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span>Modifications Allowed</span>
                      <span className="font-medium">{prop.modification_allowed ? 'Yes' : 'No'}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Historical Context */}
              {prop.historical_period && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 lg:p-6">
                  <h3 className="text-lg font-semibold text-amber-900 mb-4">Historical Context</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-amber-700">Period</span>
                      <span className="font-medium text-amber-900">{prop.historical_period}</span>
                    </div>
                    {prop.historical_region && (
                      <div className="flex justify-between">
                        <span className="text-amber-700">Region</span>
                        <span className="font-medium text-amber-900">{prop.historical_region}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 pt-2">
                      {prop.verified ? (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span className="text-green-700 text-sm font-medium">Historically Verified</span>
                        </>
                      ) : (
                        <>
                          <AlertTriangle className="h-4 w-4 text-yellow-600" />
                          <span className="text-yellow-700 text-sm font-medium">Estimated Information</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Tags */}
              {prop.tags && prop.tags.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-4 lg:p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {prop.tags.map(tag => (
                      <span key={tag} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium hover:bg-gray-200 transition-colors cursor-pointer">
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Administrative Info */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 lg:p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Administrative</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Barcode</span>
                    <span className="font-mono">{prop.barcode || 'Not assigned'}</span>
                  </div>
                  {prop.purchase_date && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Purchase Date</span>
                      <span>{new Date(prop.purchase_date).toLocaleDateString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Transfer Available</span>
                    <span className={prop.transfer_available ? 'text-green-600' : 'text-red-600'}>
                      {prop.transfer_available ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Recommendations Section - fixed at bottom */}
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-4 lg:p-6 border border-blue-200 mt-6">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="h-5 w-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">Recommendations</h3>
            </div>
            
            {recommendations ? (
              <>
                <div className="flex gap-1 mb-4 bg-white rounded-lg p-1 overflow-x-auto">
                  {[
                    { id: 'similar', label: 'Similar Items', icon: Star, count: recommendations.similar?.length || 0 },
                    { id: 'sameEra', label: 'Same Era', icon: History, count: recommendations.sameEra?.length || 0 },
                    { id: 'complementary', label: 'Complementary', icon: TrendingUp, count: recommendations.complementary?.length || 0 },
                    { id: 'fromSameOrg', label: 'Popular Here', icon: Users, count: recommendations.fromSameOrg?.length || 0 }
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveRecommendationTab(tab.id)}
                      className={`flex-shrink-0 flex items-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium transition-all ${
                        activeRecommendationTab === tab.id
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <tab.icon className="h-3 w-3" />
                      <span className="whitespace-nowrap">{tab.label}</span>
                      {tab.count > 0 && (
                        <span className={`ml-1 px-1 py-0.5 rounded-full text-xs ${
                          activeRecommendationTab === tab.id
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-200 text-gray-600'
                        }`}>
                          {tab.count}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
                
                <div className="space-y-3">
                  {recommendations[activeRecommendationTab]?.length > 0 ? (
                    <>
                      <div className="relative">
                        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide" style={{scrollbarWidth: 'none', msOverflowStyle: 'none'}}>
                          {recommendations[activeRecommendationTab].map((rec, index) => {
                            return (
                            <div 
                              key={rec.prop.id} 
                              className="flex-shrink-0 w-32 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-all duration-200 cursor-pointer group"
                              onClick={() => {
                                if (onPropClick) {
                                  onPropClick(rec.prop);
                                }
                              }}
                            >
                              <div className="aspect-square relative overflow-hidden rounded-t-lg">
                                <img 
                                  src={getPropImagePath(rec.prop)}
                                  alt={rec.prop.name}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                  onError={(e) => {
                                    if (e.target.src.includes('wasabisys.com')) {
                                      console.log('⏰ Wasabi URL likely expired');
                                    }
                                    e.target.src = '/images/fallbacks/prop-default.jpg';
                                  }}
                                />
                                <div className="absolute top-1 right-1">
                                  <div className="bg-black bg-opacity-70 text-white px-1 py-0.5 rounded text-xs font-medium">
                                    {Math.round(rec.similarity * 100)}%
                                  </div>
                                </div>
                                <div className="absolute bottom-1 left-1">
                                  <div className={`px-1 py-0.5 rounded text-xs font-medium ${
                                    rec.prop.status === 'available' 
                                      ? 'bg-green-500 text-white' 
                                      : 'bg-orange-500 text-white'
                                  }`}>
                                    {rec.prop.status === 'available' ? '✓' : '⏳'}
                                  </div>
                                </div>
                              </div>
                              
                              <div className="p-2">
                                <h4 className="font-medium text-xs text-gray-900 truncate mb-1">
                                  {rec.prop.name}
                                </h4>
                                <p className="text-xs text-gray-500 truncate mb-1">
                                  {rec.prop.category}
                                </p>
                                <p className="text-xs text-gray-400 truncate mb-1">
                                  {rec.prop.owner}
                                </p>
                                <div className="text-xs text-blue-600 truncate">
                                  {rec.reasons?.[0] || 'Recommended'}
                                </div>
                              </div>
                            </div>
                            );
                          })}
                        </div>
                        
                        {recommendations[activeRecommendationTab].length > 4 && (
                          <div className="flex justify-center mt-2">
                            <div className="text-xs text-gray-400">
                              Scroll to see more → ({recommendations[activeRecommendationTab].length} total)
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="bg-white rounded-lg p-2 border border-gray-200">
                        <div className="text-xs text-gray-600">
                          <span className="font-medium text-gray-800">
                            {activeRecommendationTab === 'similar' && '🔍 Similar characteristics'}
                            {activeRecommendationTab === 'sameEra' && `🏛️ ${prop.historical_period || 'Same era'}`}
                            {activeRecommendationTab === 'complementary' && '🎭 Works well together'}
                            {activeRecommendationTab === 'fromSameOrg' && `⭐ Popular at ${prop.owner}`}
                          </span>
                          {recommendations[activeRecommendationTab]?.[0]?.reasons?.[0] && (
                            <span className="ml-2 text-blue-600">
                              • {recommendations[activeRecommendationTab][0].reasons[0]}
                            </span>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-4 text-gray-500">
                      <div className="text-sm">No {activeRecommendationTab.replace(/([A-Z])/g, ' $1').toLowerCase()}</div>
                      <div className="text-xs mt-1">Try a different tab</div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  <div className="text-sm text-gray-600">Analyzing similarities...</div>
                </div>
              </div>
            )}
          </div>
        </Modal.Content>
    </Modal>
  );
};

export default PropDetailsModal;
